/* ═══════════════════════════════════════════════════════════════════════════
   WHEN SOMETHING THROWS

   React unmounts the whole tree when a render throws and nothing catches it.
   What the person gets is a white page — no message, no clue, and no way out
   short of knowing to open a developer console. That is what happened, and
   the absence of this file is why it could not be diagnosed from a
   screenshot.

   So: catch it, say what broke, and offer the two things that actually help.

   ── Saying what broke ────────────────────────────────────────────────────

   The message and the first few frames, on the screen, selectable. Not
   because a hairdresser will read a stack trace, but because the person who
   can fix it is one screenshot away and a white page carries nothing.

   ── And a way out ────────────────────────────────────────────────────────

   Two buttons. Reload, which fixes a transient failure. And "clear what this
   browser has kept", which fixes the other kind: this build keeps everything
   in localStorage, so a record written by an older version can be a shape
   the current one does not expect — and a person with a corrupted store
   currently has no way back at all.

   That button is deliberately the second one, and it says exactly what it
   destroys. Bookings made in this browser live nowhere else in this build,
   so clearing is not a neutral act and must not be offered as one.
   ═══════════════════════════════════════════════════════════════════════ */
import { Component } from 'react';

const KEYS = [
  'avtale_customer_v1',
  'avtale_business_picked_v1',
  'avtale_staff_picked_v1',
  'avtale_token_v1',
];

export default class Boundary extends Component {
  constructor(props) {
    super(props);
    this.state = { err: null, where: null };
  }

  static getDerivedStateFromError(err) {
    return { err };
  }

  componentDidCatch(err, info) {
    this.setState({ where: info?.componentStack || null });
    /* Also to the console, so a developer with the tab open sees the real
       thing with its source map rather than this summary. */
    // eslint-disable-next-line no-console
    console.error('Avtale.net — a surface threw:', err, info);
  }

  clear = () => {
    KEYS.forEach(k => {
      try {
        localStorage.removeItem(k);
      } catch {
        /* private mode — nothing was kept in the first place */
      }
    });
    window.location.reload();
  };

  render() {
    const { err, where } = this.state;
    if (!err) return this.props.children;

    return (
      <div className="boom">
        <div className="boom-card">
          <h1>Something on this screen threw.</h1>
          <p>
            The rest of the platform is fine — this is one screen failing, not
            the site going down. Reloading fixes most of these.
          </p>

          <pre className="boom-what">{String(err?.message || err)}</pre>

          {where && (
            <details className="boom-more">
              <summary>Where it happened</summary>
              <pre>{String(where).trim().split('\n').slice(0, 8).join('\n')}</pre>
            </details>
          )}

          <div className="console-actions">
            <button className="button dark" onClick={() => window.location.reload()}>
              RELOAD
            </button>
            <button className="button" onClick={() => { window.location.hash = ''; window.location.reload(); }}>
              Back to the front page
            </button>
          </div>

          {/* The second kind of failure, and the only way out of it. Said in
              full, because in this build a booking made in this browser
              exists nowhere else. */}
          <div className="notice">
            <b className="notice-lead">Still broken after a reload?</b>
            This build keeps everything in this browser rather than on a
            server, so something written by an older version can be a shape
            this one does not expect. Clearing starts it fresh — and it
            permanently removes every booking, business edit and message made
            in this browser. Nothing is kept anywhere else.
          </div>
          <button className="button" onClick={this.clear}>
            Clear what this browser has kept
          </button>
        </div>
      </div>
    );
  }
}
